import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { isDesktopShellEnvironment } from "../utils/desktopShell";

export type VersionDetails = {
  version: string | null;
  commit: string | null;
  builtAt: string | null;
};

type ReleaseDetails = {
  version: string | null;
  publishedAt: string | null;
};

type UpdateInfo = {
  hasUpdate: boolean;
  isLatest: boolean;
  latestVersion: string | null;
  publishedAt: string | null;
  error: string | null;
};

const normalizeString = (value: unknown): string | null => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  return null;
};

const parseSemver = (value: string | null): [number, number, number] | null => {
  if (!value) {
    return null;
  }
  const normalized = value.trim().replace(/^v/i, "");
  const match = normalized.match(/^(\d+)\.(\d+)\.(\d+)/);
  if (!match) {
    return null;
  }
  return [Number(match[1]), Number(match[2]), Number(match[3])];
};

const compareSemver = (a: [number, number, number], b: [number, number, number]) => {
  for (let i = 0; i < 3; i += 1) {
    if (a[i] > b[i]) return 1;
    if (a[i] < b[i]) return -1;
  }
  return 0;
};

const githubLatestReleaseUrl = "https://api.github.com/repos/impleotv/sky-view-release/releases/latest";

export const useServerVersion = () => {
  const { resolveApiPath } = useAuth();
  const [versionInfo, setVersionInfo] = useState<VersionDetails | null>(null);
  const [releaseInfo, setReleaseInfo] = useState<ReleaseDetails | null>(null);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [releaseFetchKey, setReleaseFetchKey] = useState(0);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadVersionInfo = async () => {
      try {
        // The embedded production UI can be served through a path-prefixed
        // reverse proxy. Try that path first, then the server's canonical
        // root route when the proxy only exposes the SPA below the prefix.
        const versionPaths = [
          ...(isDesktopShellEnvironment() ? ["/__desktop_meta"] : []),
          resolveApiPath("/api/version"),
          "/api/version",
        ];

        for (const path of new Set(versionPaths)) {
          const response = await fetch(path, { signal: controller.signal }).catch(() => null);
          if (!response) {
            continue;
          }
          if (!response.ok) {
            continue;
          }

          const payload = (await response.json().catch(() => null)) as {
            version?: unknown;
            commit?: unknown;
            builtAt?: unknown;
          } | null;

          if (!payload || typeof payload !== "object") {
            continue;
          }

          if (isMounted) {
            setVersionInfo({
              version: normalizeString(payload.version),
              commit: normalizeString(payload.commit),
              builtAt: normalizeString(payload.builtAt),
            });
          }
          return;
        }

        if (isMounted) {
          setVersionInfo(null);
        }
      } catch (error) {
        if (isMounted && !(error instanceof DOMException && error.name === "AbortError")) {
          setVersionInfo(null);
        }
      }
    };

    void loadVersionInfo();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [resolveApiPath]);

  const refreshReleaseInfo = useCallback(() => {
    setReleaseFetchKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadReleaseInfo = async () => {
      if (isMounted) {
        setReleaseLoading(true);
      }
      try {
        const response = await fetch(githubLatestReleaseUrl, {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`unexpected status ${response.status}`);
        }

        const payload = (await response.json().catch(() => null)) as {
          tag_name?: unknown;
          published_at?: unknown;
        } | null;

        if (!isMounted) {
          return;
        }

        if (!payload || typeof payload !== "object") {
          setReleaseInfo(null);
          setReleaseError("Unable to check for updates.");
          return;
        }

        setReleaseInfo({
          version: normalizeString(payload.tag_name),
          publishedAt: normalizeString(payload.published_at),
        });
        setReleaseError(null);
      } catch (error) {
        if (isMounted && !(error instanceof DOMException && error.name === "AbortError")) {
          setReleaseInfo(null);
          setReleaseError("Unable to check for updates.");
        }
      } finally {
        if (isMounted) {
          setReleaseLoading(false);
        }
      }
    };

    void loadReleaseInfo();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [releaseFetchKey]);

  const versionLabel = useMemo(() => {
    if (!versionInfo) {
      return "";
    }

    const segments: string[] = [];
    if (versionInfo.version) {
      segments.push(`Ver. ${versionInfo.version}`);
    }
    // if (versionInfo.commit) {
    //   segments.push(`Commit ${versionInfo.commit}`);
    // }
    // if (versionInfo.builtAt) {
    //   segments.push(`Built ${versionInfo.builtAt}`);
    // }

    return segments.join(" • ");
  }, [versionInfo]);

  const updateInfo: UpdateInfo = useMemo(() => {
    if (releaseError) {
      return {
        hasUpdate: false,
        isLatest: false,
        latestVersion: null,
        publishedAt: null,
        error: releaseError,
      };
    }

    if (!releaseInfo?.version) {
      return {
        hasUpdate: false,
        isLatest: false,
        latestVersion: null,
        publishedAt: null,
        error: null,
      };
    }

    const latestSemver = parseSemver(releaseInfo.version);
    const currentSemver = parseSemver(versionInfo?.version ?? null);

    if (!latestSemver || !currentSemver) {
      return {
        hasUpdate: false,
        isLatest: false,
        latestVersion: null,
        publishedAt: null,
        error: "Cannot check for updates",
      };
    }

    const comparison = compareSemver(latestSemver, currentSemver);
    if (comparison > 0) {
      return {
        hasUpdate: true,
        isLatest: false,
        latestVersion: releaseInfo.version,
        publishedAt: releaseInfo.publishedAt,
        error: null,
      };
    }

    if (comparison === 0) {
      return {
        hasUpdate: false,
        isLatest: true,
        latestVersion: releaseInfo.version,
        publishedAt: releaseInfo.publishedAt,
        error: null,
      };
    }

    return {
      hasUpdate: false,
      isLatest: false,
      latestVersion: null,
      publishedAt: null,
      error: null,
    };
  }, [releaseError, releaseInfo, versionInfo?.version]);

  return {
    versionInfo,
    versionLabel,
    updateInfo,
    refreshReleaseInfo,
    releaseLoading,
  };
};
